/**
 * Date del calendario della societa, nell'ora italiana.
 *
 * "Martedi alle 18" e un'ora del campo, non di UTC: un allenamento generato
 * sommando settimane a un istante slitterebbe di un'ora al cambio dell'ora
 * legale. Qui ogni data si converte da sola, con il fuso della societa.
 *
 * Funzioni pure come il resto del pacchetto: l'istante corrente, quando
 * serve, arriva come parametro.
 */

/** Fuso di tutte le societa: la piattaforma e italiana. */
export const FUSO_SOCIETA = 'Europe/Rome';

/** Giorni della settimana ISO: 1 lunedi, 7 domenica. */
export type GiornoSettimana = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Tetto delle date generate in un blocco solo. */
export const MAX_DATE_BLOCCO = 200;

const formatoParti = new Intl.DateTimeFormat('en-GB', {
  timeZone: FUSO_SOCIETA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function parti(istante: Date) {
  const p = Object.fromEntries(
    formatoParti.formatToParts(istante).map((x) => [x.type, Number(x.value)]),
  );
  return { anno: p.year!, mese: p.month!, giorno: p.day!, ora: p.hour!, minuto: p.minute! };
}

const due = (n: number) => String(n).padStart(2, '0');

/** Il giorno di calendario italiano di un istante, come "2026-09-17". */
export function dataLocale(istante: Date | string): string {
  const p = parti(new Date(istante));
  return `${p.anno}-${due(p.mese)}-${due(p.giorno)}`;
}

/** L'ora italiana di un istante, come "18:00". */
export function oraLocale(istante: Date | string): string {
  const p = parti(new Date(istante));
  return `${due(p.ora)}:${due(p.minuto)}`;
}

/**
 * L'istante di una data e un'ora italiane. Si parte dall'ora letta come UTC
 * e si corregge dello scarto che il fuso mostra: due passaggi bastano anche
 * a cavallo del cambio d'ora.
 */
export function istanteLocale(data: string, ora: string): Date {
  const [a, m, g] = data.split('-').map(Number) as [number, number, number];
  const [h, min] = ora.split(':').map(Number) as [number, number];
  const voluto = Date.UTC(a, m - 1, g, h, min);
  let t = voluto;
  for (let i = 0; i < 2; i++) {
    const p = parti(new Date(t));
    t += voluto - Date.UTC(p.anno, p.mese - 1, p.giorno, p.ora, p.minuto);
  }
  return new Date(t);
}

const giornoMs = 24 * 60 * 60 * 1000;
const giornoUtc = (data: string) => {
  const [a, m, g] = data.split('-').map(Number) as [number, number, number];
  return Date.UTC(a, m - 1, g);
};

/** Giorno della settimana ISO di una data di calendario. */
export function giornoSettimana(data: string): GiornoSettimana {
  return (((new Date(giornoUtc(data)).getUTCDay() + 6) % 7) + 1) as GiornoSettimana;
}

/** Il giorno `n` giorni dopo (o prima, se negativo), sul calendario e non sulle ore. */
export function aggiungiGiorni(data: string, n: number): string {
  const [y, m, d] = data.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${due(t.getUTCMonth() + 1)}-${due(t.getUTCDate())}`;
}

export type PassoCalendario = 'settimana' | 'mese';

/**
 * I periodi di calendario fra `da` e `a` compresi: settimane da lunedì a
 * domenica o mesi, con il primo e l'ultimo tagliati agli estremi.
 */
export function periodiCalendario(
  da: string,
  a: string,
  passo: PassoCalendario,
): { da: string; a: string }[] {
  const periodi: { da: string; a: string }[] = [];
  let inizio = da;
  while (inizio <= a) {
    const fine =
      passo === 'settimana'
        ? aggiungiGiorni(inizio, 7 - giornoSettimana(inizio))
        : aggiungiGiorni(`${inizio.slice(0, 7)}-01`, 31).slice(0, 7) + '-01';
    const ultimo = passo === 'settimana' ? fine : aggiungiGiorni(fine, -1);
    periodi.push({ da: inizio, a: ultimo < a ? ultimo : a });
    inizio = aggiungiGiorni(ultimo, 1);
  }
  return periodi;
}

/** Giorni di calendario tra due date, estremi compresi; zero se `a` precede `da`. */
export function giorniTra(da: string, a: string): number {
  return Math.max(0, Math.round((giornoUtc(a) - giornoUtc(da)) / giornoMs) + 1);
}

/**
 * Le date di una programmazione ricorrente: ogni giorno scelto della
 * settimana, all'ora data, tra `da` e `a` compresi. Istanti ISO in ordine.
 */
export function generaDate(regola: {
  giorni: readonly GiornoSettimana[];
  ora: string;
  da: string;
  a: string;
}): string[] {
  const scelti = new Set(regola.giorni);
  const date: string[] = [];
  for (let t = giornoUtc(regola.da); t <= giornoUtc(regola.a); t += giornoMs) {
    const d = new Date(t);
    const data = `${d.getUTCFullYear()}-${due(d.getUTCMonth() + 1)}-${due(d.getUTCDate())}`;
    if (scelti.has(giornoSettimana(data))) {
      date.push(istanteLocale(data, regola.ora).toISOString());
    }
  }
  return date;
}
